/**
 * Keeping `file` nodes pointing at the right place when a document moves.
 *
 * A canvas embeds a note or a PDF by storing its vault-relative path, so a move
 * that only touches the file itself leaves every canvas that embeds it holding
 * a dead path. This sweeps the vault and repoints them.
 */

import { type VaultTreeNode } from "../model/types.ts";
import { type VaultAdapter } from "./VaultAdapter.ts";

/**
 * The value `ref` should take after `from` moved to `to`, or null when the
 * reference points somewhere else. Handles folder moves: anything nested under
 * `from` moves with it.
 */
export function retargetPath(ref: string, from: string, to: string): string | null {
  if (ref === from) return to;
  if (ref.startsWith(`${from}/`)) return `${to}${ref.slice(from.length)}`;
  return null;
}

/**
 * Rewrite the `file` nodes of one JSON Canvas document. Returns the new text,
 * or null when the canvas doesn't reference the moved entry (so callers can
 * skip the write).
 *
 * Patches the parsed JSON in place rather than round-tripping through
 * `fromJSONCanvas`/`toJSONCanvas`: mover un archivo no tiene por qué reescribir
 * cada nodo de cada canvas del vault, y así el barrido no depende de que el
 * parser modele todo lo que el archivo pueda contener — por ejemplo un canvas
 * escrito por una versión más nueva de Blanqo.
 */
export function retargetCanvasText(text: string, from: string, to: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null; // Not JSON we can safely rewrite — leave it exactly as it is.
  }
  const nodes = (parsed as { nodes?: unknown }).nodes;
  if (!Array.isArray(nodes)) return null;

  let changed = false;
  for (const node of nodes) {
    const n = node as { file?: unknown };
    if (typeof n?.file !== "string") continue;
    const next = retargetPath(n.file, from, to);
    if (next === null) continue;
    n.file = next;
    changed = true;
  }
  return changed ? JSON.stringify(parsed, null, 2) : null;
}

/** Every `.canvas` path in the tree, depth-first. */
export function canvasPaths(nodes: VaultTreeNode[]): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    if (n.children) out.push(...canvasPaths(n.children));
    else if (n.kind === "canvas") out.push(n.path);
  }
  return out;
}

/**
 * Repoint every canvas in the vault after `from` moved to `to`. `skip` is the
 * canvas currently open, if any: its in-memory state is newer than the file, so
 * the Workspace patches that one itself and a disk write here would just be
 * overwritten by the next autosave.
 */
export async function retargetVaultRefs(
  adapter: VaultAdapter,
  tree: VaultTreeNode[],
  from: string,
  to: string,
  skip: string | null = null,
): Promise<void> {
  for (const path of canvasPaths(tree)) {
    if (path === skip) continue;
    try {
      const next = retargetCanvasText(await adapter.read(path), from, to);
      if (next !== null) await adapter.write(path, next);
    } catch {
      // An unreadable canvas shouldn't abort the move that already happened.
    }
  }
}
